'use client';

import React, {
  InputHTMLAttributes,
  forwardRef,
  useEffect,
  useRef,
} from 'react';

import { IcoMoonIcon } from '../../components/icons/icomoon';
import { cn } from '@wwe-portal/ui/lib/lib';
import { TextInput } from '../../components/input/text-input';
import { ClickableDiv } from '../../components/button';

interface SearchInputProps extends InputHTMLAttributes<HTMLInputElement> {
  onClear?: (e: React.SyntheticEvent) => void;
  wrapperClassName?: string;
  inputFocusOnMount?: boolean;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      onClear,
      className,
      wrapperClassName,
      inputFocusOnMount,
      ...props
    },
    ref
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = ref ?? internalRef;

    useEffect(() => {
      if (
        inputFocusOnMount &&
        typeof inputRef === 'object' &&
        inputRef.current
      ) {
        inputRef.current.focus();
      }
    }, [inputFocusOnMount]);

    return (
      <div className={cn('relative w-full', wrapperClassName)}>
        <IcoMoonIcon
          iconName="search"
          className="text-text700 absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2"
        />
        <TextInput
          ref={inputRef}
          className={cn(
            'bg-back300 border-back300 text-text500 font-poppins m-0 block h-[39px] w-full overflow-clip rounded-md border py-2 pl-12 pr-8 font-normal leading-[21px] outline-none transition-[border] delay-100 duration-300 ease-out',
            className
          )}
          {...props}
          value={value}
          onChange={onChange}
        />
        {value && (
          <ClickableDiv
            onClick={onClear}
            className="pointer-events-auto absolute bottom-0 right-1 top-0 flex items-center justify-center p-2"
          >
            <IcoMoonIcon iconName="close" className="text-text700 h-3 w-3" />
          </ClickableDiv>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
